import { apiIntegrations } from "@/api/integrations/integration.api";
import { DeleteListItem } from "@/components/list-item/DeleteListItem.tsx";
import { DemoUnavailable } from "@/components/demo-unavailable/DemoUnavailable.tsx";
import {
  ConfirmationModal,
  Page,
  useModals,
  useToasts,
  Button,
  Skeleton,
  Stack,
  Switch,
  Title,
} from "@skailer-com/uikit";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import {
  useIntegrationDisconnect,
  useIntegrationGoogleWorkspace,
  useIntegrationGoogleWorkspaceUpdate,
} from "@/queries/integrations/use-integrations.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiIntegrationGoogleWorkspaceSave,
  ApiIntegrationType,
} from "@/types/integrations/integration.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { parseInt } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { z } from "zod";

export const SettingsIntegrationGoogleWorkspace = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { showInfo } = useToasts();
  const { openModal } = useModals();
  const navigate = useNavigate();
  const disconnect = useIntegrationDisconnect();
  const { handleErrors } = useErrorHandle();

  useTitle(
    t("integrations:google_workspace_title"),
    t("integrations:settings_integrations_title"),
    t("common:settings")
  );

  const { data: integration, isLoading } = useIntegrationGoogleWorkspace(
    id ? parseInt(id, 10) : undefined
  );
  const SchemaObj = z.object({
    is_active: z.boolean(),
  });

  type Schema = z.infer<typeof SchemaObj>;
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<Schema>({
    mode: "onSubmit",
    resolver: zodResolver(SchemaObj),
    values: {
      is_active: integration?.is_active ?? false,
    },
  });

  const isActive = watch("is_active");

  const manageIntegration = useIntegrationGoogleWorkspaceUpdate(control);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.integrations.manageViewed, {
      [analyticProperties.source]: "Google Workspace",
    });
  });

  const onSubmit = handleSubmit(async (schema) => {
    if (!integration) return;
    const payload: ApiIntegrationGoogleWorkspaceSave = {
      is_active: schema.is_active,
      // sync_photo: schema.sync_photo,
      // sync_name: schema.sync_name,
      // sync_hired_on: schema.sync_hired_on,
      // sync_address: schema.sync_address,
      // sync_position: schema.sync_position,
      // sync_position_division: schema.sync_position_division,
    };

    try {
      await manageIntegration.mutateAsync({ id: integration.id, payload });
      analyticsService.trackEvent(analyticEvents.integrations.edited, {
        [analyticProperties.source]: "Google Workspace",
      });

      showInfo({ title: t("common:you_changes_were_saved") });
    } catch {
      return;
    }
  });

  const handleDelete = async () => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("integrations:disconnect_confirm"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        if (!integration) return;
        const result = await disconnect.mutateAsync(integration.id);

        if (result?.success) {
          analyticsService.trackEvent(analyticEvents.integrations.disconnected, {
            [analyticProperties.actionType]: "Google Workspace",
          });
          navigate(`/settings/integrations`);
        }
      },
    });
  };

  const handleConnect = async () => {
    analyticsService.trackEvent(analyticEvents.integrations.connectStarted, {
      [analyticProperties.actionType]: "Google Workspace",
    });

    const { data, error } = await apiIntegrations.install(ApiIntegrationType.google_workspace);

    if (data) {
      window.location.href = data.authorization_url;
    } else handleErrors(error);
  };

  if (integration && !integration.has_credentials) {
    return (
      <Page>
        <Page.Header
          title={t("integrations:google_workspace_title")}
          showBack={true}
          navigateTo={"/settings/integrations"}
        />
        <Page.Content>
          <Stack gap={"xl"} className={"container-sm mx-auto"}>
            <Title
              header={t("integrations:google_workspace_not_connected")}
              caption={t("integrations:google_workspace_not_connected_description")}
              paddingBottom
              paddingTop
            />
            <Stack gap={"sm"}>
              <DemoUnavailable className="w-full">
                <Button onClick={handleConnect} size={"lg"} className={"w-full"}>
                  {t("integrations:connect")}
                </Button>
              </DemoUnavailable>
            </Stack>
          </Stack>
        </Page.Content>
      </Page>
    );
  }

  return (
    <Page>
      <Page.Header
        title={t("integrations:slack_title")}
        showBack={true}
        navigateTo={"/settings/integrations"}
      />
      <Page.Content>
        <Stack gap={"xl"} className={"container-sm mx-auto"}>
          {isLoading && <Skeleton className={"w-full h-24"} />}
          <form onSubmit={onSubmit} className={"flex flex-col grow"}>
            <Title header={t("integrations:manage")} paddingBottom paddingTop />
            <Stack gap={"sm"}>
              <Controller
                render={({ field: { value, onChange } }) => (
                  <Switch
                    checked={value}
                    onChange={(newValue) => {
                      onChange(newValue);
                    }}
                    title={t("integrations:active_switch")}
                    description={t("integrations:active_switch_description")}
                  />
                )}
                control={control}
                name={"is_active"}
              />
            </Stack>
            {isActive && (
              <>
                <Stack gap={"md"} className={"pt-8"}>
                  {/*{integration && !integration.is_all_scopes_installed && (*/}
                  {/*  <Title*/}
                  {/*    header={t("integrations:slack_reconnection_required_title")}*/}
                  {/*    caption={t("integrations:slack_reconnection_required_description")}*/}
                  {/*  />*/}
                  {/*)}*/}

                  <DemoUnavailable>
                    <Button onClick={handleConnect}>{t("integrations:reconnect")}</Button>
                  </DemoUnavailable>
                </Stack>
              </>
            )}
            <ServerErrorField errors={errors} />
          </form>
          <DeleteListItem
            onDelete={handleDelete}
            title={t("integrations:disconnect") + " Google Workspace"}
            caption={integration?.identity ?? undefined}
            deleteTitle={t("integrations:disconnect")}
          />

          <Button
            onClick={onSubmit}
            isLoading={manageIntegration.isPending}
            size={"lg"}
            className={"w-full"}
            type={"submit"}
          >
            {t("common:save")}
          </Button>
        </Stack>
      </Page.Content>
    </Page>
  );
};
